import type { IImageData } from './dotcms-models/dotcms-image';

export type IImageCropParameters = {
  widthInPixels: number;
  heightInPixels: number;
};

// This is implemented based on the following documentation:
// https://www.dotcms.com/docs/latest/image-resizing-and-processing
// https://www.dotcms.com/docs/latest/image-resizing-and-processing#advancedImageApi
const dotcmsImageTool = () => ({
  resizeWidth: (pixels: number) => `/${pixels}w`,

  resizeHeight: (pixels: number) => `/${pixels}h`,

  cropWidth: (pixels: number) => `/${pixels}cw`,

  cropHeight: (pixels: number) => `/${pixels}ch`,

  // The focal point of the image, as a percentage, from the top left corner of the image.
  // Use with crop width and crop height to create images built around a focal point.
  focalPoint: (percentageFromLeft: number, percentageFromTop: number) =>
    `/fp/${percentageFromLeft},${percentageFromTop}`,

  focalPointFromString: (focalPoint: string) => `/fp/${focalPoint}`,

  toJpeg: () => '/jpeg',

  toProgressiveJpeg: () => '/jpegp',

  toWebp: () => '/webp',
});

export const resizeDotcmsImage = (
  image: IImageData,
  parameters: IImageCropParameters,
): string => {
  if (image.url.endsWith('svg')) {
    // If the image is an SVG, we should just return the plain unmodified svg URL.
    // (Dotcms makes the image look blurry and terrible if you try to use these APIs on an SVG)
    return image.url;
  }

  const dotcmsTool = dotcmsImageTool();
  const imageUrl = new URL(image.url);
  let imagePath = imageUrl.pathname;
  const focalPoint =
    image.focalPoint?.split(',').map((x) => Number.parseFloat(x)) ?? undefined;
  const requestedWidth = parameters.widthInPixels;
  const requestedHieght = parameters.heightInPixels;

  // Adjust requested width and height based on the device's pixel density
  const devicePixelRatio = window.devicePixelRatio || 1;
  const adjustedWidth = requestedWidth * devicePixelRatio;
  const adjustedHeight = requestedHieght * devicePixelRatio;

  // Crop the maximum rectangle from the original image to the requested side ratio
  let width: number;
  let height: number;
  const requestedWidthToHeightRatio = adjustedWidth / adjustedHeight;
  const initialWidthToHeightRatio = image.width / image.height;
  if (requestedWidthToHeightRatio > initialWidthToHeightRatio) {
    width = image.width;
    height = Math.round(image.width / requestedWidthToHeightRatio);
  } else {
    width = Math.round(image.height * requestedWidthToHeightRatio);
    height = image.height;
  }
  imagePath += dotcmsTool.cropWidth(width);
  imagePath += dotcmsTool.cropHeight(height);

  // Set the faces in the image as the center point of the image, this should be set in dotcms
  if (focalPoint) {
    const focalPointX = width >= image.width ? 0.5 : focalPoint[0];
    const focalPointY = height >= image.height ? 0.5 : focalPoint[1];
    imagePath += dotcmsTool.focalPoint(focalPointX, focalPointY);
  } else if (image.focalPoint) {
    imagePath += dotcmsTool.focalPointFromString(image.focalPoint);
  }

  /*
    Resize is required if the original image is bigger than the requested/adjusted one.
    Adjusted value equals to requested in case Device Pixel Ratio (DPR) is 1.
    Example 1:
    Original image is 1000x1000, requested image is 150x150, DPR is 1
    Result is 1000x1000 resized to 150x150
    Example 2:
    Original image is 1000x1000, requested image is 150x150, DPR is 2
    Result is 1000x1000 resized and adjusted to 300x300
    Example 3:
    Original image is 1000x1000, requested image is 2000x2000, DPR is 1
    Result is 1000x1000
  */
  if (width > adjustedWidth && height > adjustedHeight) {
    imagePath += dotcmsTool.resizeWidth(adjustedWidth);
    imagePath += dotcmsTool.resizeHeight(adjustedHeight);
  }

  imagePath += dotcmsTool.toWebp();

  const newImageUrl = new URL(
    `${imageUrl.protocol}${imageUrl.host}${imagePath}${imageUrl.search}`,
  );
  return newImageUrl.href;
};
